import { useState, useEffect, useCallback, useRef } from 'react';
import { addToQueue, getPendingQueue, getPendingCount, markAllSynced } from 'utils/scannerDB';
import { syncOffline } from 'services/qrService';

const useOfflineQueue = () => {
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing]           = useState(false);
  const syncingRef                      = useRef(false);

  const refreshCount = useCallback(async () => {
    const count = await getPendingCount();
    setPendingCount(count);
  }, []);

  // Sincroniza todos los registros pendientes con datofy-api
  const syncPending = useCallback(async () => {
    if (syncingRef.current || !navigator.onLine) return;
    syncingRef.current = true;
    setSyncing(true);

    try {
      const pending = await getPendingQueue();
      if (!pending.length) return;

      // Preparar payload sin el campo local_id
      const records = pending.map(({ local_id, synced, ...rest }) => rest);

      const res = await syncOffline(records);

      // El endpoint siempre devuelve success:true aunque algún registro haya
      // fallado individualmente (ver `errors[]`) — antes se confiaba en ese
      // flag y se marcaba TODO el lote como sincronizado, incluyendo
      // registros que en realidad nunca se guardaron en el servidor
      // (pérdida silenciosa). Ahora solo se marcan los índices que el
      // backend confirma explícitamente en `confirmed_indexes` (creados de
      // verdad o ya existentes por dedup) — el resto queda en la cola y se
      // reintenta en el próximo evento online.
      if (res.success && Array.isArray(res.confirmed_indexes)) {
        const confirmedIds = res.confirmed_indexes
          .map(i => pending[i]?.local_id)
          .filter(id => id !== undefined);
        await markAllSynced(confirmedIds);
        await refreshCount();
      }
    } catch {
      // Silencioso — se reintentará en el próximo evento online
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, [refreshCount]);

  // Encolar un registro offline
  const enqueue = useCallback(async (logData) => {
    await addToQueue(logData);
    await refreshCount();
  }, [refreshCount]);

  // Auto-sync al recuperar conexión
  useEffect(() => {
    const handleOnline = () => syncPending();
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [syncPending]);

  // Reintento periódico — el evento 'online' de arriba no alcanza: un
  // registro puede quedar en la cola por un fallo puntual de la API (timeout,
  // 500 pasajero) sin que el dispositivo haya estado realmente sin red, así
  // que nunca ocurre una transición offline→online que dispare ese listener
  // y el pendiente queda ahí indefinidamente hasta un click manual. Mientras
  // haya pendientes, se reintenta solo cada 30s — syncPending() ya no hace
  // nada si navigator.onLine es false, así que es seguro dejarlo corriendo.
  useEffect(() => {
    if (pendingCount === 0) return undefined;
    const id = setInterval(() => { syncPending(); }, 30000);
    return () => clearInterval(id);
  }, [pendingCount, syncPending]);

  // Cargar conteo inicial
  useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  return { pendingCount, syncing, enqueue, syncPending };
};

export default useOfflineQueue;
